import api from 'services/api';
import { normalizeForAPI } from 'utils/companyNormalizer';
import requestDeduplicationService from 'services/requestDeduplicationService';

/**
 * Buscar dados do dashboard financeiro
 * @returns {Promise<any>} Dados do dashboard
 */
export async function buscarDashboardFinanceiro() {
  try {
    const response = await api.get('/dashboard/financeiro');

    
    return normalizarResumo(response.data);
  } catch (error) {
    console.error('Erro ao buscar dashboard financeiro:', error);
    throw error;
  }
}

function normalizarResumo(data) {
  if (!data) return {};

  return {
    titulos_emitidos: data.titulos_emitidos ?? data.total_titulos_emitidos ?? 0,
    valor_emitido_total:
      data.valor_emitido_total ?? data.valor_total_emitido ?? 0,
    titulos_recebidos:
      data.titulos_recebidos ?? data.total_titulos_recebidos ?? 0,
    valor_pago_total: data.valor_pago_total ?? data.valor_total_recebido ?? 0,
    titulos_em_aberto:
      data.titulos_em_aberto ?? data.total_titulos_a_receber ?? 0,
    valor_em_aberto_total:
      data.valor_em_aberto_total ?? data.valor_restante_a_receber ?? 0,
    percentual_recebido:
      data.percentual_recebido ?? data.percentual_pago ?? data.percentual ?? 0,
  };
}

/**
 * Buscar dados de evolução mensal financeira
 */
export async function buscarGraficoEvolucaoMensal(params = {}) {
  try {
    // Usar deduplicação para evitar requisições duplicadas
    const response = await requestDeduplicationService.executeRequest(
      (url, requestParams) => api.post(url, requestParams),
      '/n8n/financeiro/grafico-evolucao-mensal',
      {
        empresa: normalizeForAPI(params.empresa) || 'MOV Fibra',
        data_inicio: params.data_inicio || getDataInicioMes(),
        data_fim: params.data_fim || getDataFimMes()
      },
      'POST',
      30000 // 30 segundos de timeout
    );

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao buscar dados financeiros',
      data: []
    };
  }
}

/**
 * Buscar resumo financeiro
 */
export async function buscarResumoFinanceiro(params = {}) {
  try {
    // Usar deduplicação para evitar requisições duplicadas
    const response = await requestDeduplicationService.executeRequest(
      (url, requestParams) => api.post(url, requestParams),
      '/n8n/financeiro/resumo',
      {
        empresa: normalizeForAPI(params.empresa) || 'MOV Fibra',
        data_inicio: params.data_inicio || getDataInicioMes(),
        data_fim: params.data_fim || getDataFimMes()
      },
      'POST',
      30000 // 30 segundos de timeout
    );

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao buscar resumo financeiro',
      data: {}
    };
  }
}

/**
 * Buscar dashboard financeiro completo (recomendado)
 */
export async function buscarDashboardCompleto(params = {}) {
  try {
    const response = await api.get('/n8n/financeiro/gerar-dashboard-completo', {
      params: {
        empresa: normalizeForAPI(params.empresa) || 'MOV Fibra',
        data_inicio: params.data_inicio || getDataInicioMes(),
        data_fim: params.data_fim || getDataFimMes()
      }
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao buscar dashboard completo',
      data: []
    };
  }
}

/**
 * Buscar dashboard financeiro detalhado
 */
export async function buscarDashboardDetalhado(params = {}) {
  try {
    const tipoFinanceiro = params.tipo_financeiro || 'todos';
    // Usar deduplicação para evitar requisições duplicadas
    const response = await requestDeduplicationService.executeRequest(
      (url, requestParams) => api.post(url, requestParams),
      '/n8n/financeiro/dashboard-detalhado',
      {
        action: 'gerar_dashboard_detalhado',
        data_inicio: params.data_inicio || getDataInicioMes(),
        data_fim: params.data_fim || getDataFimMes(),
        empresa: normalizeForAPI(params.empresa) || 'MOV Fibra',
        mes_vencimento: params.mes_vencimento || (new Date().getMonth() + 1).toString(),
        tipo_financeiro: tipoFinanceiro
      },
      'POST',
      90000 // 90 segundos de timeout (alinhado com backend)
    );

    // Verificar se a resposta tem a estrutura SWR esperada
    if (response.data && response.data.success && response.data.data) {
      return {
        success: true,
        data: response.data.data,
        cached: response.data.cached || false,
        stale: response.data.stale || false,
        multitenant: response.data.multitenant || null,
        etag: response.data.etag,
        updated_at: response.data.updated_at
      };
    } else if (response.data && response.data.data) {
      // Estrutura direta sem success (fallback)
      return {
        success: true,
        data: response.data.data,
        cached: false,
        stale: false
      };
    } else {
      // Fallback para estrutura antiga
      return {
        success: true,
        data: response.data,
        cached: false,
        stale: false
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao buscar dashboard detalhado',
      data: null
    };
  }
}

// =============================
// Recebimentos (novas rotas)
// =============================

function getPeriodoPadrao() {
  const hoje = new Date();
  const data_inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
  const data_fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
  return { data_inicio, data_fim };
}

function unwrapReceita(payload, key) {
  // Aceita formatos: {success,data:[{key:[..]}]} ou [{key:[..]}] ou {key:[..]}
  if (!payload) return [];
  if (payload.data && Array.isArray(payload.data) && payload.data[0] && payload.data[0][key]) {
    return payload.data[0][key] || [];
  }
  if (Array.isArray(payload) && payload[0] && payload[0][key]) {
    return payload[0][key] || [];
  }
  if (payload[key]) return payload[key] || [];
  return [];
}

function unwrapFirstArrayCollection(payload) {
  if (!payload) return [];

  // Padrão N8N comum: { success: true, data: [ { qualquer_coisa: [...] } ] }
  const first = payload?.data?.[0];
  if (first && typeof first === 'object' && !Array.isArray(first)) {
    const arrayKey = Object.keys(first).find((k) => Array.isArray(first[k]));
    if (arrayKey) return first[arrayKey] || [];
  }

  // Se data já for uma lista de linhas
  if (payload.data && Array.isArray(payload.data) && payload.data.length > 0 && typeof payload.data[0] === 'object' && !Array.isArray(payload.data[0])) {
    return payload.data;
  }

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;

  return [];
}

function actionToCollectionKey(action) {
  return ({
    por_conta_receita: 'conta_receita',
    por_conta_carteira: 'conta_carteira',
    por_usuarios: 'por_usuarios',
  }[action]) || null;
}

export async function buscarRecebimentosPorAction(action, params = {}) {
  try {
    const p = {
      action,
      empresa: normalizeForAPI(params.empresa) || 'MOV Fibra',
      ...(params.data_inicio && params.data_fim ? params : getPeriodoPadrao()),
    };

    const response = await requestDeduplicationService.executeRequest(
      (url, body) => api.post(url, body),
      '/n8n/financeiro/recebimentos',
      p,
      'POST',
      30000
    );

    const collectionKey = actionToCollectionKey(action);
    if (!collectionKey) {
      return { success: true, data: unwrapFirstArrayCollection(response.data) };
    }

    return { success: true, data: unwrapReceita(response.data, collectionKey) };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data || 'Erro ao buscar recebimentos',
      data: []
    };
  }
}

export async function buscarRecebimentosContaReceita(params = {}) {
  try {
    const p = {
      empresa: normalizeForAPI(params.empresa) || 'MOV Fibra',
      ...(params.data_inicio && params.data_fim ? params : getPeriodoPadrao()),
    };
    const response = await requestDeduplicationService.executeRequest(
      (url, body) => api.post(url, body),
      '/n8n/financeiro/recebimentos-conta-receita',
      p,
      'POST',
      30000
    );
    return { success: true, data: unwrapReceita(response.data, 'conta_receita') };
  } catch (error) {
    return { success: false, error: error.response?.data || 'Erro ao buscar recebimentos por conta de receita', data: [] };
  }
}

export async function buscarRecebimentosContaCarteira(params = {}) {
  try {
    const p = {
      empresa: normalizeForAPI(params.empresa) || 'MOV Fibra',
      ...(params.data_inicio && params.data_fim ? params : getPeriodoPadrao()),
    };
    const response = await requestDeduplicationService.executeRequest(
      (url, body) => api.post(url, body),
      '/n8n/financeiro/recebimentos-conta-carteira',
      p,
      'POST',
      30000
    );
    return { success: true, data: unwrapReceita(response.data, 'conta_carteira') };
  } catch (error) {
    return { success: false, error: error.response?.data || 'Erro ao buscar recebimentos por conta/carteira', data: [] };
  }
}

export async function buscarRecebimentosPorUsuarios(params = {}) {
  try {
    const p = {
      empresa: normalizeForAPI(params.empresa) || 'MOV Fibra',
      ...(params.data_inicio && params.data_fim ? params : getPeriodoPadrao()),
    };
    const response = await requestDeduplicationService.executeRequest(
      (url, body) => api.post(url, body),
      '/n8n/financeiro/recebimentos-por-usuarios',
      p,
      'POST',
      30000
    );
    return { success: true, data: unwrapReceita(response.data, 'por_usuarios') };
  } catch (error) {
    return { success: false, error: error.response?.data || 'Erro ao buscar recebimentos por usuários', data: [] };
  }
}

export async function buscarRecebimentosCapabilities(params = {}) {
  try {
    const p = {
      empresa: normalizeForAPI(params.empresa) || 'MOV Fibra',
    };

    const response = await requestDeduplicationService.executeRequest(
      (url, requestParams) => api.get(url, { params: requestParams }),
      '/n8n/financeiro/recebimentos-capabilities',
      p,
      'GET',
      30000
    );

    const actions = response.data?.actions;
    if (Array.isArray(actions)) {
      return { success: true, actions };
    }

    return { success: true, actions: [] };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data || 'Erro ao buscar capabilities de recebimentos',
      actions: []
    };
  }
}

// Funções auxiliares
function getDataInicioMes() {
  const hoje = new Date();
  return new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
}

function getDataFimMes() {
  const hoje = new Date();
  return new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
}

// ========================================
// FUNÇÕES DO CACHE HISTÓRICO - ETAPA 5
// ========================================

/**
 * Buscar dados históricos financeiros
 */
export async function buscarDadosHistoricos(params = {}) {
  try {
    const response = await api.post('/financeiro-historico/buscar-dados-historicos', {
      meses: params.meses || 11,
      empresa: normalizeForAPI(params.empresa) || 'MOV Fibra'
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao buscar dados históricos',
      data: null
    };
  }
}

/**
 * Verificar status do cache histórico
 */
export async function verificarStatusCache() {
  try {
    const response = await api.post('/financeiro-historico/verificar-status', {});

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao verificar status do cache',
      data: null
    };
  }
}

/**
 * Buscar dados consolidados
 * NOTA: Esta função não é mais necessária pois a consolidação é feita
 * pelo financeiroConsolidadoService que busca dados individuais de cada empresa
 */
export async function buscarDadosConsolidados(_params = {}) {
  console.warn('⚠️ buscarDadosConsolidados está obsoleta. Use financeiroConsolidadoService.buscarDadosConsolidados()');
  
  return {
    success: false,
    error: 'Função obsoleta. Use financeiroConsolidadoService.buscarDadosConsolidados()',
    data: null
  };
}

/**
 * Listar meses disponíveis
 */
export async function listarMesesDisponiveis() {
  try {
    const response = await api.post('/financeiro-historico/listar-meses', {});

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao listar meses disponíveis',
      data: []
    };
  }
}

/**
 * Health check do cache
 */
export async function healthCheckCache() {
  try {
    const response = await api.post('/financeiro-historico/health-check', {});

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro no health check do cache',
      data: null
    };
  }
}

const financeiroService = {

  buscarDashboardFinanceiro,

  buscarGraficoEvolucaoMensal,

  buscarResumoFinanceiro,

  buscarDashboardCompleto,

  buscarDashboardDetalhado,

  // Novas funções do cache histórico

  buscarDadosHistoricos,

  verificarStatusCache,

  buscarDadosConsolidados,

  listarMesesDisponiveis,

  healthCheckCache

};



export default financeiroService;
